import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RoomPlacement } from '@/Data/World';
import { useOrnament, usePlacement } from '@/Recoil/World';
import { GRID_SIZE, CELL_SIZE, HORIZONTAL_PADDING, TOP_PADDING, DEFAULT_WALLPAPER_ID, gridToPixel } from './gridUtils';
import LoadingCircle from '@/Components/LoadingCircle';
import './IsometricRoom.scss';

interface IsometricRoomProps {
    addressid: string;
}

const BASE_WIDTH = GRID_SIZE * CELL_SIZE + HORIZONTAL_PADDING * 2; // 470px
const BASE_HEIGHT = BASE_WIDTH * 540 / 470; // Maintain aspect ratio

const IsometricRoom: React.FC<IsometricRoomProps> = ({ addressid }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    const { ornaments, wallpapers, isLoaded: ornamentsLoaded } = useOrnament();
    const { placementsByUser, loading: placementsLoading } = usePlacement();

    const placements = useMemo(() => placementsByUser[addressid] || [], [placementsByUser, addressid]);
    const loading = !ornamentsLoaded || placementsLoading;

    // Auto-scale based on container width using ResizeObserver
    useEffect(() => {
        const updateScale = () => {
            if (containerRef.current) {
                const parentHeight = containerRef.current.parentElement?.getBoundingClientRect().height || BASE_HEIGHT;
                setScale(parentHeight < BASE_HEIGHT ? parentHeight / BASE_HEIGHT : 1);
            }
        };

        // Use ResizeObserver for reliable initial and resize detection
        const resizeObserver = new ResizeObserver(() => {
            updateScale();
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        // Also run once immediately after mount
        updateScale();

        return () => {
            resizeObserver.disconnect();
        };
    }, [loading]);

    // Get active wallpaper
    const activeWallpaper = useMemo(() => {
        const wallpaperPlacement = placements.find(p => p.ornamentid in wallpapers);
        if (wallpaperPlacement) { return wallpapers[wallpaperPlacement.ornamentid]; }
        return wallpapers[DEFAULT_WALLPAPER_ID];
    }, [placements, wallpapers]);

    // Filter ornament placements (exclude wallpapers)
    const ornamentPlacements = useMemo(() => {
        return placements.filter(p => p.ornamentid in ornaments);
    }, [placements, ornaments]);

    const getPlacementStyle = (placement: RoomPlacement): React.CSSProperties => {
        const ornament = ornaments[placement.ornamentid];
        if (!ornament) { return {}; }

        const { left, top } = gridToPixel(placement.x, placement.y);

        return {
            position: 'absolute',
            left,
            top,
            width: `${ornament.width * CELL_SIZE}px`,
            height: `${ornament.height * CELL_SIZE}px`,
            transform: placement.direction === 'left' ? 'scaleX(-1)' : 'none',
            zIndex: 10
        };
    };

    if (loading) {
        return (
            <div ref={containerRef} className="IsometricRoom Loading">
                <LoadingCircle show />
            </div>
        );
    }

    return (
        <div ref={containerRef} className="IsometricRoom" style={{
        }}>
            <div
                className="IsometricRoomInner"
                style={{
                    width: BASE_WIDTH,
            transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                }}
            >
                {/* Wallpaper background */}
                {activeWallpaper && (
                    <img
                        src={activeWallpaper.image}
                        alt="Room wallpaper"
                        className="Wallpaper"
                    />
                )}

                {/* Ornament layer */}
                <div
                    className="OrnamentLayer"
                    style={{
                        left: HORIZONTAL_PADDING,
                        top: TOP_PADDING,
                        width: GRID_SIZE * CELL_SIZE,
                        height: GRID_SIZE * CELL_SIZE
                    }}
                >
                    {ornamentPlacements.map(placement => {
                        const ornament = ornaments[placement.ornamentid];
                        if (!ornament) { return null; }
                        return (
                            <div
                                key={placement.ornamentid}
                                className="PlacedOrnament"
                                style={getPlacementStyle(placement)}
                            >
                                <img
                                    src={ornament.image}
                                    alt={ornament.name}
                                    className="OrnamentImage"
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default IsometricRoom;
